import { useEffect, useRef, useState } from "react";
import { loadGoogleClientId, loadGoogleIdentity } from "../lib/google.ts";
import { gameEdition } from "../lib/edition.ts";
import { isNativeApp } from "../lib/native.ts";
import { registerPlugin } from "@capacitor/core";
import { messages } from "../i18n/index.ts";

const googleIdentity = registerPlugin<{ signIn(options: { clientId: string }): Promise<{ credential: string }> }>("GoogleIdentity");

interface GoogleSignInButtonProps {
  /** Google'ın imzalı kimlik jetonu — sunucuda doğrulanacak. */
  onCredential: (credential: string) => void;
  /** Düğme yazısı. Silme onayında "devam et" yerine "doğrula" anlamı taşır. */
  text?: "signin_with" | "signup_with" | "continue_with";
  disabled?: boolean;
  /** Verilirse düğmenin altına bu etiketli ayraç çizilir — yalnızca düğme varsa. */
  dividerLabel?: string;
}

/**
 * Google'ın kendi çizdiği giriş düğmesi.
 *
 * Düğmenin görünümünü Google'ın betiği bir iframe içinde üretiyor; markanın
 * kullanım kurallarına uymanın en güvenli yolu bu. Betik yüklenemezse (çevrimdışı,
 * engellenmiş) hiçbir şey çizilmez — şifreyle giriş yolu zaten duruyor.
 */
export function GoogleSignInButton({
  onCredential,
  text = "continue_with",
  disabled = false,
  dividerLabel,
}: GoogleSignInButtonProps) {
  const host = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [nativeClientId, setNativeClientId] = useState<string | null>(null);

  // En güncel geri çağrı: GIS'e verilen kapanış bir kez bağlanıyor.
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const clientId = await loadGoogleClientId();
      if (cancelled || !clientId) return;

      if (isNativeApp()) {
        setNativeClientId(clientId);
        setReady(true);
        return;
      }

      const identity = await loadGoogleIdentity();
      if (cancelled || !identity || !host.current) return;

      identity.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response.credential) callbackRef.current(response.credential);
        },
        // Tek dokunuşla otomatik giriş sürprizi olmasın; kullanıcı düğmeye bassın.
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      host.current.replaceChildren();
      identity.renderButton(host.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "rectangular",
        text,
        logo_alignment: "center",
        locale: gameEdition.locale,
        width: Math.min(400, Math.max(200, host.current.clientWidth || 320)),
      });
      setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [text]);

  // Hazır değilken yer kaplamasın: Google kapalıysa ekranda boşluk kalmasın.
  return (
    <div
      className="google-block"
      data-ready={ready ? "true" : undefined}
      data-disabled={disabled ? "true" : undefined}
    >
      {isNativeApp() ? (
        nativeClientId && <button type="button" className="btn-secondary w-full" disabled={disabled} onClick={() => {
          void googleIdentity.signIn({ clientId: nativeClientId })
            .then(({ credential }) => callbackRef.current(credential))
            .catch(() => { /* User cancelled or Play services unavailable. */ });
        }}>{messages.profile.googleButton}</button>
      ) : <div ref={host} className="google-block__button" />}
      {dividerLabel && (
        <div className="google-divider" aria-hidden="true">
          <span>{dividerLabel}</span>
        </div>
      )}
    </div>
  );
}
